(() => {
  "use strict";

  const AVAIL_PARAM = "filter.v.availability";
  const AVAIL_VALUE = "1";

  const __dcgLoaded = window.__dcgLoaded || (window.__dcgLoaded = new Set());

  function loadScriptOnce(src) {
    return new Promise((resolve, reject) => {
      if (!src) return reject(new Error("Missing script src"));
      if (__dcgLoaded.has(src)) return resolve();

      const exists = [...document.scripts].some((s) => s.src === src);
      if (exists) {
        __dcgLoaded.add(src);
        return resolve();
      }

      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = () => { __dcgLoaded.add(src); resolve(); };
      s.onerror = () => reject(new Error("Failed to load " + src));
      document.head.appendChild(s);
    });
  }

  function currentSearchParamsWithoutPage() {
    const url = new URL(window.location.href);
    url.searchParams.delete("page");
    return url.searchParams;
  }

  function setHrefWithMergedParams(anchor, keepParams) {
    try {
      const raw = anchor.getAttribute("href");
      if (!raw || raw === "#" || raw.startsWith("javascript:")) return;

      const u = new URL(raw, window.location.origin);

      for (const [k, v] of keepParams.entries()) {
        if (k === "page") continue;
        if (k === AVAIL_PARAM) u.searchParams.set(k, v);
        else if (!u.searchParams.has(k)) u.searchParams.set(k, v);
      }

      u.searchParams.delete("page");
      anchor.setAttribute("href", u.pathname + (u.searchParams.toString() ? `?${u.searchParams.toString()}` : ""));
    } catch (_) {}
  }

  function patchBinderLinks(mount) {
    const keepParams = currentSearchParamsWithoutPage();

    const patchAll = () => {
      mount.querySelectorAll("a[href]").forEach((a) => setHrefWithMergedParams(a, keepParams));
    };

    patchAll();
    new MutationObserver(patchAll).observe(mount, { childList: true, subtree: true });
  }

  function initStockToggle(root) {
    const checkbox = root.querySelector("#DcgInStockOnly");
    if (!checkbox) return;

    const url = new URL(window.location.href);
    checkbox.checked = url.searchParams.get(AVAIL_PARAM) === AVAIL_VALUE;

    checkbox.addEventListener("change", () => {
      const next = new URL(window.location.href);
      next.searchParams.delete("page");
      if (checkbox.checked) next.searchParams.set(AVAIL_PARAM, AVAIL_VALUE);
      else next.searchParams.delete(AVAIL_PARAM);
      window.location.href = next.toString();
    });
  }

  function initClearLink(root) {
    const clear = root.querySelector("[data-dcg-clear]");
    if (!clear) return;

    const keepParams = currentSearchParamsWithoutPage();
    const u = new URL(clear.getAttribute("href") || window.location.pathname, window.location.origin);

    for (const [k, v] of keepParams.entries()) {
      if (k === "page") continue;
      u.searchParams.set(k, v);
    }

    clear.setAttribute("href", u.pathname + (u.searchParams.toString() ? `?${u.searchParams.toString()}` : ""));
  }

  function initDrawer(root) {
    const openBtn = root.querySelector("[data-dcg-open]");
    const closeBtn = root.querySelector("[data-dcg-close]");
    const overlay = root.querySelector("[data-dcg-overlay]");

    const open = () => root.classList.add("dcg-filters--open");
    const close = () => root.classList.remove("dcg-filters--open");

    openBtn && openBtn.addEventListener("click", open);
    closeBtn && closeBtn.addEventListener("click", close);
    overlay && overlay.addEventListener("click", close);

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") close();
    });
  }

  function setStatus(mount, txt) {
    const el = mount.querySelector("[data-dcg-binder-status]");
    if (el) el.textContent = txt;
  }

  function hasInjectedUI(mount) {
    return !!(mount.querySelector("strong") || mount.querySelector("ul li a") || mount.querySelector(".custom-checkbox a"));
  }

  async function initBinder(root) {
    const mount = root.querySelector("[data-dcg-binder-mount]");
    if (!mount) return;

    patchBinderLinks(mount);

    if (mount.dataset.dcgInit === "1") return;
    mount.dataset.dcgInit = "1";

    const collectionType = (mount.dataset.collectionType || "").trim();
    const binderSrc = mount.dataset.binderSrc;

    if (!collectionType) {
      setStatus(mount, "Filters unavailable (missing type).");
      return;
    }

    // Clear cached empty (common Binder issue)
    try {
      const raw = localStorage.getItem(collectionType);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length === 0) localStorage.removeItem(collectionType);
      }
    } catch (_) {}

    setStatus(mount, "Loading filters…");

    // Ensure CollectionFilters exists
    if (!window.CollectionFilters) {
      try {
        await loadScriptOnce(binderSrc);
      } catch (e) {
        console.warn("[DCG Filters] Failed to load collectionFilters.js", e);
        setStatus(mount, "Filters failed to load (script).");
        return;
      }
    }

    if (!window.CollectionFilters) {
      setStatus(mount, "Filters unavailable.");
      return;
    }

    const tagsEl = document.getElementById(mount.dataset.tagsJsonId);
    const activeEl = document.getElementById(mount.dataset.activeJsonId);

    const options = {
      divToAppendTo: "#DcgBinderFilters",
      collectionType,
      activeTags: activeEl ? activeEl.textContent.trim() : "[]",
      requestPath: mount.dataset.requestPath || window.location.pathname,
      collectionHandle: mount.dataset.collectionHandle || "",
      collectionTags: tagsEl ? tagsEl.textContent.trim() : "[]",
    };

    try {
      const sidebarFilters = new window.CollectionFilters(options);
      sidebarFilters.init();
    } catch (e) {
      console.warn("[DCG Filters] Binder init error", e);
      setStatus(mount, "Filters error.");
      return;
    }

    // Confirm injection, retry once if empty
    setTimeout(() => {
      if (hasInjectedUI(mount)) {
        const s = mount.querySelector("[data-dcg-binder-status]");
        if (s) s.remove();
        return;
      }
      // Retry once after clearing cache
      try { localStorage.removeItem(collectionType); } catch (_) {}
      mount.dataset.dcgInit = "0";
      initBinder(root);
    }, 800);
  }

  /* ─────────────────────────────────────────────────────────────────────────
     SET NUMBER FILTER
     ─────────────────────────────────────────────────────────────────────────
     Reads collection.tags (embedded as JSON by dcg-sidebar-filters.liquid),
     identifies tags that look like TCG set/collector numbers, and renders
     a live-search text input with matching suggestions.

     Tag patterns recognised as set numbers:
       "001/264"   Pokémon / MTG style   (digits + "/" + digits)
       "SV001"     Pokémon promo style   (letters + digits, no spaces)
       "LEDE-EN001" YGO style            (letters + dash + letters + digits)
       "001"       bare numeric only     (≤ 4 digits, leading zeros common)

     URL routing mirrors collectionFilters.js exactly:
       No active tags → /collections/{handle}/{tag-handle}
       Active tags    → {requestPath}+{tag-handle}
  ───────────────────────────────────────────────────────────────────────── */

  // Mirrors collectionFilters.js handleize() exactly so URLs match
  function dcgHandleize(str) {
    str = str.toLowerCase();
    ['"', "'", "\\", "(", ")", "[", "]"].forEach((c) => { str = str.split(c).join(""); });
    str = str.replace(/[^a-z0-9é]+/g, "-");
    if (str.endsWith("-")) str = str.slice(0, -1);
    if (str.startsWith("-")) str = str.slice(1);
    str = str.replace(/ø/g, "o").replace(/æ/g, "ae").replace(/å/g, "a");
    return str;
  }

  // Returns true if a tag looks like a TCG set/collector number
  function isSetNumberTag(tag) {
    const t = tag.trim();
    // "001/264", "1/165", "SV001/SV264" — anything with digits/digits
    if (/^\d[\d\/]+\d$/.test(t)) return true;
    // Bare numeric 1–4 digits (e.g. "001", "42")
    if (/^\d{1,4}$/.test(t)) return true;
    // YGO style: LEDE-EN001, PHHY-JP023, etc.
    if (/^[A-Z]{2,6}-[A-Z]{2}\d{3}$/i.test(t)) return true;
    // Pokémon promo style: SV001, PR-SV001
    if (/^[A-Z]{1,5}-?[A-Z]{0,2}\d{3}$/i.test(t)) return true;
    return false;
  }

  function initSetNumberFilter(root) {
    const mount = root.querySelector("[data-dcg-set-number-filter]");
    if (!mount || mount.dataset.snfInit === "1") return;
    mount.dataset.snfInit = "1";

    const collectionHandle = mount.dataset.collectionHandle || "all";
    const requestPath      = mount.dataset.requestPath || window.location.pathname;
    const tagsJsonId       = mount.dataset.tagsJsonId;
    const activeJsonId     = mount.dataset.activeJsonId;

    // Parse the tag JSON blobs embedded by Liquid
    let allTags = [];
    let activeTags = [];
    try {
      const tagsEl  = tagsJsonId  ? document.getElementById(tagsJsonId)  : null;
      const activeEl = activeJsonId ? document.getElementById(activeJsonId) : null;
      if (tagsEl)  allTags    = JSON.parse(tagsEl.textContent.trim()).map((t) => t.tag || t);
      if (activeEl) activeTags = JSON.parse(activeEl.textContent.trim());
    } catch (_) { return; }

    // Filter down to set-number-shaped tags
    const setNumberTags = allTags.filter(isSetNumberTag).sort((a, b) => {
      // Sort numerically on the leading digits
      const numA = parseInt(a, 10) || 0;
      const numB = parseInt(b, 10) || 0;
      return numA !== numB ? numA - numB : a.localeCompare(b);
    });

    if (!setNumberTags.length) return; // Nothing to show — hide the widget entirely

    // Check if a set number tag is currently active
    const activeSetNumber = activeTags.find((t) => isSetNumberTag(typeof t === "string" ? t : (t.tag || "")));

    // ── Build the widget ──────────────────────────────────────────────────
    const wrapper = document.createElement("div");
    wrapper.className = "dcg-snf";

    const heading = document.createElement("strong");
    heading.className = "dcg-snf__heading";
    heading.textContent = "Set Number";
    wrapper.appendChild(heading);

    // Active tag pill + clear link
    if (activeSetNumber) {
      const activeTag = typeof activeSetNumber === "string" ? activeSetNumber : (activeSetNumber.tag || "");
      const pill = document.createElement("div");
      pill.className = "dcg-snf__active";

      const pillLabel = document.createElement("span");
      pillLabel.className = "dcg-snf__active-label";
      pillLabel.textContent = activeTag;

      // Clear = navigate to collection without this tag segment
      const clearUrl = requestPath.replace("/" + dcgHandleize(activeTag), "")
        .replace(/\/+$/, "") || "/collections/" + collectionHandle;

      const clearLink = document.createElement("a");
      clearLink.className = "dcg-snf__active-clear";
      clearLink.href = clearUrl || "/collections/" + collectionHandle;
      clearLink.setAttribute("aria-label", "Remove set number filter " + activeTag);
      clearLink.innerHTML = "&#x2715;"; // ✕

      pill.appendChild(pillLabel);
      pill.appendChild(clearLink);
      wrapper.appendChild(pill);
    }

    // Text input
    const inputWrap = document.createElement("div");
    inputWrap.className = "dcg-snf__input-wrap";

    const input = document.createElement("input");
    input.type = "text";
    input.className = "dcg-snf__input";
    input.placeholder = "e.g. 001 or 001/264";
    input.setAttribute("aria-label", "Filter by set number");
    if (activeSetNumber) {
      const activeTag = typeof activeSetNumber === "string" ? activeSetNumber : (activeSetNumber.tag || "");
      input.value = activeTag;
    }
    inputWrap.appendChild(input);
    wrapper.appendChild(inputWrap);

    // Suggestions list (hidden until input has value)
    const list = document.createElement("ul");
    list.className = "dcg-snf__list";
    list.setAttribute("role", "listbox");
    list.setAttribute("aria-label", "Set number suggestions");
    list.hidden = true;
    wrapper.appendChild(list);

    mount.appendChild(wrapper);

    // ── Render suggestions matching the current input value ───────────────
    function renderSuggestions(query) {
      list.innerHTML = "";
      const q = query.trim().toLowerCase();
      if (!q) { list.hidden = true; return; }

      const matches = setNumberTags.filter((tag) => tag.toLowerCase().includes(q));
      if (!matches.length) { list.hidden = true; return; }

      matches.slice(0, 30).forEach((tag) => {
        const handle = dcgHandleize(tag);
        const isActive = activeTags.some((at) => {
          const atStr = typeof at === "string" ? at : (at.tag || "");
          return atStr.toLowerCase() === tag.toLowerCase();
        });

        // Mirror collectionFilters.js URL logic exactly
        const href = isActive
          ? "#"
          : (activeTags.length === 0
              ? "/collections/" + collectionHandle + "/" + handle
              : requestPath + "+" + handle);

        const li = document.createElement("li");
        li.className = "dcg-snf__item" + (isActive ? " is-active" : "");
        li.setAttribute("role", "option");
        li.setAttribute("aria-selected", String(isActive));

        const a = document.createElement("a");
        a.href = href;
        a.className = "dcg-snf__link";
        a.textContent = tag;
        if (isActive) a.setAttribute("title", "Remove " + tag + " filter");

        li.appendChild(a);
        list.appendChild(li);
      });

      list.hidden = false;
    }

    // Debounce input
    let debounceTimer = null;
    input.addEventListener("input", () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => renderSuggestions(input.value), 220);
    });

    input.addEventListener("focus", () => {
      if (input.value.trim()) renderSuggestions(input.value);
    });

    // Close suggestions on outside click
    document.addEventListener("click", (e) => {
      if (!wrapper.contains(e.target)) { list.hidden = true; }
    });
  }

  function initAll(scope = document) {
    // Original path: dcg-sidebar-filters.liquid wrapper (data-mode="binder")
    scope.querySelectorAll("[data-dcg-filters][data-mode='binder']").forEach((root) => {
      initDrawer(root);
      initStockToggle(root);
      initClearLink(root);
      initSetNumberFilter(root);
      initBinder(root);
    });

    // sidebar.liquid path: mount point rendered directly with no wrapper element.
    // initSetNumberFilter accepts any element containing [data-dcg-set-number-filter].
    // Pass the section root so we find the mount wherever it lives on the page.
    const directMount = scope.querySelector("[data-dcg-set-number-filter]");
    if (directMount && !directMount.closest("[data-dcg-filters]")) {
      // Wrap in a plain object that querySelector can be called on
      initSetNumberFilter({ querySelector: (sel) => scope.querySelector(sel) });
    }
  }

  // Run even if script loads after DOMContentLoaded
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }
  document.addEventListener("shopify:section:load", (e) => initAll(e.target));
})();